import { useState, useEffect, useRef } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { loadSettingsFromSupabase } from "@/lib/settingsSync";
import { saveUserSettings, getLastLocalSettingsWriteTs } from "@/lib/userSettings";
import { setCurrentUserId, getCurrentUserId } from "@/lib/userScopedStorage";
import { loadLearningFromCloud } from "@/lib/aacLearningSync";
import { getPredictionEngine } from "@/lib/aacPredictionEngine";

// Grace window: if a local settings write happened within this many ms,
// trust local over the cloud row to avoid clobbering a fresh change
// (e.g. language change immediately followed by a page reload).
const LOCAL_WRITE_GRACE_MS = 15_000;

/**
 * After per-user namespacing of all caches in `userScopedStorage`, the
 * sign-in flow is straightforward:
 *  1. Tell the storage layer which user is now active (so getScoped/
 *     setScoped target `<key>:<userId>`).
 *  2. Pull cloud → local, unless a local write happened in the last few
 *     seconds (race after reload of a fresh setting).
 *
 * On sign-out we just clear the active user id; the previous user's
 * namespaced data remains intact, so multi-account on one device works.
 */

async function hydrateForUser(userId: string): Promise<void> {
  try {
    const cloudSettings = await loadSettingsFromSupabase(userId);
    if (cloudSettings) {
      const sinceLocalWrite = Date.now() - getLastLocalSettingsWriteTs();
      if (sinceLocalWrite > LOCAL_WRITE_GRACE_MS) {
        saveUserSettings(cloudSettings);
      }
    }

    const cloudLearning = await loadLearningFromCloud(userId);
    if (cloudLearning) {
      const engine = getPredictionEngine();
      engine.setCloudData(cloudLearning);
    }
  } catch (error) {
    console.error("[useAuth] Settings sync error:", error);
  }
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const lastUserIdRef = useRef<string | null>(getCurrentUserId());

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        if (event === 'SIGNED_OUT') {
          setCurrentUserId(null);
          lastUserIdRef.current = null;
          return;
        }

        if (event === 'SIGNED_IN' && session?.user) {
          const userId = session.user.id;
          // Switch the storage namespace BEFORE any read/write so that
          // settings sync targets the correct per-user bucket.
          setCurrentUserId(userId);
          lastUserIdRef.current = userId;
          setTimeout(() => { void hydrateForUser(userId); }, 0);
        }
      }
    );

    // Existing session check (handles page reloads).
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      if (session?.user) {
        const userId = session.user.id;
        setCurrentUserId(userId);
        lastUserIdRef.current = userId;
        setTimeout(() => { void hydrateForUser(userId); }, 0);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    // Defensive: SIGNED_OUT handler also runs, but ensure namespace is
    // cleared synchronously so the UI never reads stale data.
    setCurrentUserId(null);
  };

  return { user, session, loading, signOut };
}
